import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { businessUnitService } from 'src/data/api/orbit-service/businessUnitService';
import { divisionService } from 'src/data/api/orbit-service/divisionService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  BusinessUnit,
  BusinessUnitWithDivisions,
  CreateBusinessUnitRequest,
  DeleteBusinessUnitRequest,
  ListBusinessUnitRequest,
  UpdateBusinessUnitRequest,
} from 'src/types/orbit/BusinessUnit';
import { Division } from 'src/types/orbit/Division';
import { handleError } from 'src/utils/generalUtils';

export interface BusinessUnitState {
  businessUnitList: BusinessUnit[];
  businessUnitsWithDivisions: BusinessUnitWithDivisions[];
  fetchBusinessUnitApiStatus: ApiRequestStatus;
  fetchBusinessUnitsWithDivisionsApiStatus: ApiRequestStatus;
  updateBusinessUnitApiStatus: BusinessUnitApiStatus;
  deleteBusinessUnitApiStatus: BusinessUnitApiStatus;
  createBusinessUnitApiStatus: BusinessUnitApiStatus;
}

interface BusinessUnitApiStatus extends ApiRequestStatus {
  businessUnitId?: string;
}

const initialState: BusinessUnitState = {
  businessUnitList: [],
  businessUnitsWithDivisions: [],
  fetchBusinessUnitApiStatus: initApiStatus,
  fetchBusinessUnitsWithDivisionsApiStatus: initApiStatus,
  deleteBusinessUnitApiStatus: initApiStatus,
  updateBusinessUnitApiStatus: initApiStatus,
  createBusinessUnitApiStatus: initApiStatus,
};

export const fetchBusinessUnit = createAsyncThunk(
  'businessUnit/fetchBusinessUnit',
  async (data: ListBusinessUnitRequest, { rejectWithValue }) => {
    try {
      const response = await businessUnitService.getAll(data);
      return response.businessUnits;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const fetchBusinessUnitsWithDivisionsApiThunk = createAsyncThunk(
  'businessUnit/fetchBusinessUnitsWithDivisions',
  async (data: ListBusinessUnitRequest, { rejectWithValue }) => {
    try {
      let businessUnitsWithDivisions: BusinessUnitWithDivisions[] = [];
      let nextToken = undefined;
      if (data.businessUnitId) {
        const businessUnitResponse = await businessUnitService.get({
          businessUnitId: data.businessUnitId!,
          programId: data.programId,
        });
        businessUnitsWithDivisions = [
          {
            ...businessUnitResponse.businessUnit,
            divisions: [],
            isDivisionsLoading: true,
            divisionsError: null,
          },
        ];
      } else {
        // Fetch business units
        const businessUnitsResponse = await businessUnitService.getAll(data);
        nextToken = businessUnitsResponse.nextToken;
        // // Create business units with divisions array
        businessUnitsWithDivisions = businessUnitsResponse.businessUnits.map((bu) => ({
          ...bu,
          divisions: [],
          isDivisionsLoading: true,
          divisionsError: null,
        }));
      }

      // Fetch all divisions in parallel
      await Promise.all(
        businessUnitsWithDivisions.map(async (businessUnit) => {
          try {
            const divisionsResponse = await divisionService.getAllByBusinessUnit({
              businessUnitId: businessUnit.businessUnitId,
              programId: data.programId,
              isDeleted: false,
            });
            businessUnit.divisions = divisionsResponse.divisions;
            businessUnit.isDivisionsLoading = false;
          } catch (error: unknown) {
            businessUnit.isDivisionsLoading = false;
            businessUnit.divisionsError = handleError(error);
          }
        }),
      );

      return {
        businessUnitsWithDivisions: businessUnitsWithDivisions,
        nextToken: nextToken,
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteBusinessUnitApiThunk = createAsyncThunk(
  'businessUnit/deleteBu',
  async (data: DeleteBusinessUnitRequest, { rejectWithValue }) => {
    try {
      const response = await businessUnitService.delete(data);
      return {
        businessUnitId: response.businessUnitId,
        message: 'Delete business successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateBusinessUnitApiThunk = createAsyncThunk(
  'businessUnit/updateBU',
  async (data: UpdateBusinessUnitRequest, { rejectWithValue }) => {
    try {
      const response = await businessUnitService.update(data);
      return {
        businessUnit: response.businessUnit,
        message: 'update business unit successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createBusinessUnitApiThunk = createAsyncThunk(
  'businessUnit/createBu',
  async (data: CreateBusinessUnitRequest, { rejectWithValue }) => {
    try {
      const response = await businessUnitService.create(data);
      return {
        businessUnit: response.businessUnit,
        message: 'business created successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);
const businessUnitSlice = createSlice({
  name: 'BusinessUnit',
  initialState,
  reducers: {
    clearBusinessUnitMessages: (state) => {
      state.createBusinessUnitApiStatus.errorMessage = '';
      state.createBusinessUnitApiStatus.successMessage = '';

      state.updateBusinessUnitApiStatus.errorMessage = '';
      state.updateBusinessUnitApiStatus.successMessage = '';

      state.deleteBusinessUnitApiStatus.errorMessage = '';
      state.deleteBusinessUnitApiStatus.successMessage = '';
    },
    removeFromBUDivisions: (state, action: PayloadAction<string>) => {
      state.businessUnitsWithDivisions = state.businessUnitsWithDivisions.filter(
        (bu) => bu.businessUnitId !== action.payload,
      );
    },
    updateBusinessUnit: (state, action: PayloadAction<any>) => {
      state.businessUnitsWithDivisions = state.businessUnitsWithDivisions.map((bu) =>
        bu.businessUnitId === action.payload.businessUnit.businessUnitId
          ? { ...bu, ...action.payload.businessUnit }
          : bu,
      );
    },
    addToBUwithDivisions: (state, action: PayloadAction<any>) => {
      state.businessUnitsWithDivisions.push({
        ...action.payload,
        divisions: [],
        isDivisionsLoading: true,
        divisionsError: null,
      });
    },
    addDivisionToBusinessUnit: (state, action: PayloadAction<any>) => {
      const { businessUnitId, division } = action.payload;
      const businessUnitIndex = state.businessUnitsWithDivisions.findIndex(
        (bu) => bu.businessUnitId === businessUnitId,
      );
      if (businessUnitIndex !== -1) {
        state.businessUnitsWithDivisions[businessUnitIndex].divisions.push(division);
      }
    },

    updateDivisionToBusinessUnit: (state, action: PayloadAction<any>) => {
      const division = action.payload as Division;

      state.businessUnitsWithDivisions = _.map(
        state.businessUnitsWithDivisions,
        (businessUnit) => {
          if (businessUnit.businessUnitId === division.businessUnit) {
            return {
              ...businessUnit,
              divisions: _.map(businessUnit.divisions, (item) =>
                item.divisionName === division.divisionName ? division : item,
              ),
            };
          }
          return businessUnit;
        },
      );
    },
    deleteDivisionFromBusinessUnit: (state, action: PayloadAction<any>) => {
      const division = action.payload as Division;

      state.businessUnitsWithDivisions = _.map(
        state.businessUnitsWithDivisions,
        (businessUnit) => {
          if (businessUnit.businessUnitId === division.businessUnit) {
            return {
              ...businessUnit,
              divisions: _.filter(
                businessUnit.divisions,
                (item) => item.divisionName !== division.divisionName,
              ),
            };
          }
          return businessUnit;
        },
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchBusinessUnit actions
      .addCase(fetchBusinessUnit.pending, (state) => {
        state.fetchBusinessUnitApiStatus.isLoading = true;
        state.fetchBusinessUnitApiStatus.errorMessage = null;
      })
      .addCase(fetchBusinessUnit.fulfilled, (state, action) => {
        state.fetchBusinessUnitApiStatus.isLoading = false;
        state.businessUnitList = action.payload;
      })
      .addCase(fetchBusinessUnit.rejected, (state, action) => {
        state.fetchBusinessUnitApiStatus.isLoading = false;
        state.fetchBusinessUnitApiStatus.errorMessage = action.payload as string;
      })

      // Handle fetchBusinessUnitsWithDivisions actions
      .addCase(fetchBusinessUnitsWithDivisionsApiThunk.pending, (state) => {
        state.businessUnitsWithDivisions = [];
        state.fetchBusinessUnitsWithDivisionsApiStatus.isLoading = true;
        state.fetchBusinessUnitsWithDivisionsApiStatus.errorMessage = null;
      })
      .addCase(fetchBusinessUnitsWithDivisionsApiThunk.fulfilled, (state, action) => {
        state.fetchBusinessUnitsWithDivisionsApiStatus.isLoading = false;
        state.businessUnitsWithDivisions = action.payload.businessUnitsWithDivisions;
        state.fetchBusinessUnitsWithDivisionsApiStatus.nextToken =
          action.payload.nextToken;
      })
      .addCase(fetchBusinessUnitsWithDivisionsApiThunk.rejected, (state, action) => {
        state.fetchBusinessUnitsWithDivisionsApiStatus.isLoading = false;
        state.fetchBusinessUnitsWithDivisionsApiStatus.errorMessage =
          action.payload as string;
      })

      // handle businessUnit update
      .addCase(updateBusinessUnitApiThunk.pending, (state, action) => {
        state.updateBusinessUnitApiStatus.businessUnitId = action.meta.arg.businessUnitId;
        state.updateBusinessUnitApiStatus.isLoading = true;
        state.updateBusinessUnitApiStatus.errorMessage = null;
        state.updateBusinessUnitApiStatus.successMessage = null;
      })
      .addCase(updateBusinessUnitApiThunk.fulfilled, (state, action) => {
        state.updateBusinessUnitApiStatus.isLoading = false;
        state.updateBusinessUnitApiStatus.isSuccess = true;
        state.updateBusinessUnitApiStatus.successMessage = action.payload.message;
        businessUnitSlice.caseReducers.updateBusinessUnit(state, {
          type: 'updateBusinessUnit',
          payload: action.payload,
        });
      })
      .addCase(updateBusinessUnitApiThunk.rejected, (state, action) => {
        state.updateBusinessUnitApiStatus.isLoading = false;
        state.updateBusinessUnitApiStatus.isSuccess = false;
        state.updateBusinessUnitApiStatus.errorMessage = action.payload as string;
        state.updateBusinessUnitApiStatus.successMessage = null;
      })

      // handle businessUnit delete
      .addCase(deleteBusinessUnitApiThunk.pending, (state, action) => {
        state.deleteBusinessUnitApiStatus.businessUnitId = action.meta.arg.businessUnitId;
        state.deleteBusinessUnitApiStatus.isLoading = true;
        state.deleteBusinessUnitApiStatus.errorMessage = null;
        state.deleteBusinessUnitApiStatus.successMessage = null;
      })
      .addCase(deleteBusinessUnitApiThunk.fulfilled, (state, action) => {
        state.deleteBusinessUnitApiStatus.isLoading = false;
        state.deleteBusinessUnitApiStatus.isSuccess = true;
        state.deleteBusinessUnitApiStatus.successMessage = action.payload.message;
        businessUnitSlice.caseReducers.removeFromBUDivisions(state, {
          type: 'removeFromBUDivisions',
          payload: action.payload.businessUnitId,
        });
      })

      .addCase(deleteBusinessUnitApiThunk.rejected, (state, action) => {
        state.deleteBusinessUnitApiStatus.isLoading = false;
        state.deleteBusinessUnitApiStatus.isSuccess = false;
        state.deleteBusinessUnitApiStatus.errorMessage = action.payload as string;
        state.deleteBusinessUnitApiStatus.successMessage = null;
      })

      // handel create new businessUnit

      .addCase(createBusinessUnitApiThunk.pending, (state, action) => {
        state.createBusinessUnitApiStatus.isSuccess = false;
        state.createBusinessUnitApiStatus.isLoading = true;
        state.createBusinessUnitApiStatus.errorMessage = null;
        state.createBusinessUnitApiStatus.successMessage = null;
      })
      .addCase(createBusinessUnitApiThunk.fulfilled, (state, action) => {
        state.createBusinessUnitApiStatus.isLoading = false;
        state.createBusinessUnitApiStatus.isSuccess = true;
        state.createBusinessUnitApiStatus.successMessage = action.payload.message;

        businessUnitSlice.caseReducers.addToBUwithDivisions(state, {
          type: 'addToBUwithDivisions',
          payload: action.payload.businessUnit,
        });
      })
      .addCase(createBusinessUnitApiThunk.rejected, (state, action) => {
        state.createBusinessUnitApiStatus.isLoading = false;
        state.createBusinessUnitApiStatus.isSuccess = false;
        state.createBusinessUnitApiStatus.errorMessage = action.payload as string;
        state.createBusinessUnitApiStatus.successMessage = null;
      });
  },
});

export const {
  deleteDivisionFromBusinessUnit,
  removeFromBUDivisions,
  addToBUwithDivisions,
  addDivisionToBusinessUnit,
  updateDivisionToBusinessUnit,
  clearBusinessUnitMessages,
} = businessUnitSlice.actions;
export default businessUnitSlice.reducer;
